import { useState, useEffect, useRef } from 'react';
import { 
  getElementos, 
  getElementById, 
  createElement, 
  updateElement, 
  patchElement, 
  deleteElement,
  getCamposModelo,
  getColumnasTabla
} from '@shared/helpers/api/mantenedores/generalesCrud';
import { getRegiones, getComunas } from '@shared/helpers/api/mantenedores/generales';
import Swal from 'sweetalert2';

/**
 * Hook avanzado para manejar mantenedores generales con funcionalidad CRUD completa
 */
export const useGeneralesCrud = (modelo, user, permisos) => {
  // Estados principales
  const [allData, setAllData] = useState([]); // Todos los datos sin filtrar
  const [data, setData] = useState([]); // Datos filtrados y paginados para mostrar
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados de paginación y filtros
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  
  // Estados para sorting y filtros de columnas
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  
  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Estados para datos dependientes (select options)
  const [dependentData, setDependentData] = useState({
    regiones: [],
    comunas: [],
    clientes: []
  });

  // Control de cancelación de requests
  const abortControllerRef = useRef();
  const lastFetchId = useRef(0);

  // Configuración del modelo
  const campos = getCamposModelo(modelo);
  const columnas = getColumnasTabla(modelo);

  // ================================
  // FUNCIONES DE PAGINACIÓN LOCAL
  // ================================

  /**
   * Filtrar datos según búsqueda
   */
  const filterData = (dataToFilter, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return dataToFilter;
    }

    const searchLower = searchTerm.toLowerCase();
    return dataToFilter.filter(item => {
      return campos.some(campo => {
        const value = item[campo.name];
        if (value == null) return false;
        return value.toString().toLowerCase().includes(searchLower);
      });
    });
  };

  /**
   * Aplicar paginación, filtros y sorting localmente
   */
  const applyLocalPaginationAndFilter = () => {
    let processedData = [...allData];
    
    // 1. Aplicar filtros de búsqueda general
    if (searchValue) {
      processedData = filterData(processedData, searchValue);
    }
    
    // 2. Aplicar filtros por columna
    Object.keys(filters).forEach(columnKey => {
      const filterValue = filters[columnKey];
      if (filterValue && filterValue !== '') {
        processedData = processedData.filter(item => {
          const getValue = (item, key) => {
            if (key.includes('.')) {
              const keys = key.split('.');
              let value = item;
              for (const k of keys) {
                value = value?.[k];
                if (value == null) return null;
              }
              return value;
            }
            return item[key];
          };
          
          const value = getValue(item, columnKey);
          if (value == null) return false;
          const filterValueStr = filterValue.toString().toLowerCase();
          return value.toString().toLowerCase().includes(filterValueStr);
        });
      }
    });
    
    // 3. Aplicar sorting
    if (sortConfig.key) {
      processedData.sort((a, b) => {
        const getValue = (item, key) => {
          if (key.includes('.')) {
            const keys = key.split('.');
            let value = item;
            for (const k of keys) {
              value = value?.[k];
              if (value == null) return null;
            }
            return value;
          }
          return item[key];
        };
        
        const aValue = getValue(a, sortConfig.key);
        const bValue = getValue(b, sortConfig.key);
        
        // Manejar valores null/undefined
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
        
        // Comparar valores
        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = aValue.toString().toLowerCase().localeCompare(bValue.toString().toLowerCase());
        }
        
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    
    // 4. Calcular paginación
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = processedData.slice(startIndex, endIndex);
    
    // 5. Actualizar estados
    setData(paginatedData);
    setTotal(processedData.length);
  };

  // ================================
  // FUNCIONES DE DATOS
  // ================================

  /**
   * Cargar datos dependientes para selects
   */
  const loadDependentData = async () => {
    try {
      // Verificar si el modelo necesita regiones o comunas
      const needsRegiones = campos.some(campo => campo.options === 'regiones');
      const needsComunas = campos.some(campo => campo.options === 'comunas');
      const needsClientes = campos.some(campo => campo.options === 'clientes');
      
      if (needsRegiones) {
        const regionesResponse = await getRegiones(user);
        if (regionesResponse.success) {
          setDependentData(prev => ({
            ...prev,
            regiones: regionesResponse.data
          }));
        }
      }
      
      if (needsComunas) {
        const comunasResponse = await getComunas(user);
        if (comunasResponse.success) {
          setDependentData(prev => ({
            ...prev,
            comunas: comunasResponse.data
          }));
        }
      }

      if (needsClientes) {
        const clientesResponse = await getElementos(user, 'clientes', { pageSize: 1000 });
        if (clientesResponse.success) {
          setDependentData(prev => ({
            ...prev,
            clientes: clientesResponse.data
          }));
        }
      }
    } catch (error) {
      console.error('Error cargando datos dependientes:', error);
    }
  };

  /**
   * Obtener todos los datos principales (sin paginación)
   */
  const fetchData = async (signal) => {
    try {
      setLoading(true);
      setError(null);
      
      // Enviar pageSize grande para obtener todos los datos
      const response = await getElementos(user, modelo, { pageSize: 1000 });
      
      if (signal && signal.aborted) return;
      
      if (response.success) {
        setAllData(response.data || []);
        // No aplicamos paginación aquí, se aplicará en useEffect
      } else {
        setError(response.error || 'Error al cargar datos');
        setAllData([]);
      }
    } catch (err) {
      if (signal && signal.aborted) return;
      console.error('Error en fetchData:', err);
      setError(err.message || 'Error al cargar datos');
      setAllData([]);
    } finally {
      if (signal && !signal.aborted) {
        setLoading(false);
      }
    }
  };

  /**
   * Refrescar datos
   */
  const refreshData = () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchData(controller.signal);
  };

  // ================================
  // FUNCIONES DE FORMULARIO
  // ================================

  /**
   * Inicializar formulario
   */
  const initializeForm = (item = null) => {
    const initialData = {};
    
    campos.forEach(campo => {
      if (item) {
        initialData[campo.name] = item[campo.name] || '';
      } else {
        initialData[campo.name] = campo.type === 'number' ? 0 : '';
      }
    });
    
    setFormData(initialData);
    setFormErrors({});
  };

  /**
   * Validar formulario
   */
  const validateForm = () => {
    const errors = {};
    
    campos.forEach(campo => {
      if (campo.required && (!formData[campo.name] || formData[campo.name].toString().trim() === '')) {
        errors[campo.name] = `${campo.label} es requerido`;
      }
      
      if (campo.type === 'number' && formData[campo.name] !== '') {
        const value = parseFloat(formData[campo.name]);
        if (isNaN(value)) {
          errors[campo.name] = `${campo.label} debe ser un número válido`;
        } else {
          if (campo.min !== undefined && value < campo.min) {
            errors[campo.name] = `${campo.label} debe ser mayor o igual a ${campo.min}`;
          }
          if (campo.max !== undefined && value > campo.max) {
            errors[campo.name] = `${campo.label} debe ser menor o igual a ${campo.max}`;
          }
        }
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Manejar cambios en el formulario
   */
  const handleFormChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Limpiar error del campo si existe
    if (formErrors[fieldName]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // ================================
  // FUNCIONES CRUD
  // ================================

  /**
   * Crear elemento
   */
  const handleCreate = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      const response = await createElement(user, modelo, formData);
      
      if (response.success) {
        await Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: response.message || 'Elemento creado exitosamente',
          timer: 2000,
          showConfirmButton: false
        });
        
        setShowCreateModal(false);
        refreshData();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.error || 'Error al crear elemento'
        });
      }
    } catch (error) {
      console.error('Error en handleCreate:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error inesperado al crear elemento'
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Actualizar elemento
   */
  const handleUpdate = async () => {
    if (!validateForm() || !selectedItem) return;
    
    setSubmitting(true);
    
    try {
      const response = await patchElement(user, modelo, selectedItem.id, formData);
      
      if (response.success) {
        await Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: response.message || 'Elemento actualizado exitosamente',
          timer: 2000,
          showConfirmButton: false
        });
        
        setShowEditModal(false);
        setSelectedItem(null);
        refreshData();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.error || 'Error al actualizar elemento'
        });
      }
    } catch (error) {
      console.error('Error en handleUpdate:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error inesperado al actualizar elemento'
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Eliminar elemento
   */
  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Esta acción eliminará permanentemente este ${modelo}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteElement(user, modelo, item.id);
        
        if (response.success) {
          await Swal.fire({
            icon: 'success',
            title: '¡Eliminado!',
            text: response.message || 'Elemento eliminado exitosamente',
            timer: 2000,
            showConfirmButton: false
          });
          
          refreshData();
        } else {
          await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.error || 'Error al eliminar elemento'
          });
        }
      } catch (error) {
        console.error('Error en handleDelete:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error inesperado al eliminar elemento'
        });
      }
    }
  };

  // ================================
  // FUNCIONES DE SORTING Y FILTROS
  // ================================

  /**
   * Manejar cambio de sorting
   */
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /**
   * Manejar cambio de filtro por columna
   */
  const handleFilterChange = (columnKey, value) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
    // Resetear a la primera página cuando se aplica un filtro
    setPageNumber(1);
  };

  /**
   * Limpiar filtro específico
   */
  const clearFilter = (columnKey) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnKey];
      return newFilters;
    });
  };

  /**
   * Limpiar todos los filtros
   */
  const clearAllFilters = () => {
    setFilters({});
    setSortConfig({ key: null, direction: 'asc' });
    setPageNumber(1);
  };

  /**
   * Obtener valores únicos para filtros de columna
   */
  const getUniqueValues = (columnKey) => {
    if (!allData || allData.length === 0) return [];
    
    const getValue = (item, key) => {
      if (key.includes('.')) {
        const keys = key.split('.');
        let value = item;
        for (const k of keys) {
          value = value?.[k];
          if (value == null) return null;
        }
        return value;
      }
      return item[key];
    };
    
    const values = allData
      .map(item => getValue(item, columnKey))
      .filter(value => value != null && value !== '')
      .map(value => value.toString());
    
    return [...new Set(values)].sort();
  };

  // ================================
  // FUNCIONES DE MODALES
  // ================================

  /**
   * Abrir modal de creación
   */
  const openCreateModal = () => {
    if (!permisos.canCreate) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin permisos',
        text: 'No tienes permisos para crear elementos'
      });
      return;
    }
    
    initializeForm();
    setShowCreateModal(true);
  };

  /**
   * Abrir modal de visualización
   */
  const openViewModal = (item) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  /**
   * Abrir modal de edición
   */
  const openEditModal = (item) => {
    if (!permisos.canEdit) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin permisos',
        text: 'No tienes permisos para editar elementos'
      });
      return;
    }
    
    setSelectedItem(item);
    initializeForm(item);
    setShowViewModal(false);
    setShowEditModal(true);
  };

  /**
   * Cerrar modales
   */
  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setSelectedItem(null);
    setFormData({});
    setFormErrors({});
  };

  // ================================
  // HANDLERS DE TABLA
  // ================================

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const handleSearch = (newSearchValue) => {
    setSearchValue(newSearchValue);
    setPageNumber(1);
  };

  const handleRowClick = (item) => {
    openViewModal(item);
  };

  // ================================
  // EFFECTS
  // ================================

  // Cargar todos los datos inicialmente
  useEffect(() => {
    // Cancelar request anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [modelo, user]);

  // Aplicar filtros, sorting y paginación localmente cuando cambian los parámetros
  useEffect(() => {
    // Solo filtrar si searchValue tiene 2+ caracteres o está vacío
    if (searchValue.length > 0 && searchValue.length < 2) {
      setData([]);
      setTotal(0);
      return;
    }

    applyLocalPaginationAndFilter();
  }, [allData, pageNumber, pageSize, searchValue, sortConfig, filters]);

  // Cargar datos dependientes al montar
  useEffect(() => {
    loadDependentData();
  }, [modelo, user]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Calcular total de páginas
  const totalPages = Math.ceil(total / pageSize);

  // Métodos compatibles con las páginas
  const handleEdit = (item) => {
    openEditModal(item);
  };

  const handleCreateNew = () => {
    openCreateModal();
  };

  const closeModal = () => {
    closeModals();
  };

  const handleSave = (data) => {
    if (showCreateModal) {
      return handleCreate(data);
    } else if (showEditModal) {
      return handleUpdate(data);
    }
  };

  // Estados de modal unificados
  const showModal = showCreateModal || showEditModal || showViewModal;
  const modalMode = showCreateModal ? 'create' : (showEditModal ? 'edit' : 'view');
  const selectedElement = selectedItem;
  const savingElement = submitting;

  return {
    // Estados de datos
    data,
    allData, // Todos los datos sin paginación
    total,
    loading,
    error,
    pageSize,
    pageNumber,
    searchValue,
    totalPages,
    
    // Estados de modales unificados
    showModal,
    modalMode,
    selectedElement,
    savingElement,
    
    // Configuración del modelo
    campos,
    columnas,
    dependentData,
    
    // Estados del formulario
    formData,
    formErrors,
    
    // Estados de sorting y filtros
    sortConfig,
    filters,
    
    // Handlers de tabla
    handlePageChange,
    handlePageSizeChange,
    handleSearch,
    handleRowClick,
    
    // Handlers de sorting y filtros
    onSort: handleSort,
    onFilterChange: handleFilterChange,
    clearFilter,
    clearAllFilters,
    getUniqueValues,
    
    // Handlers compatibles con las páginas
    handleEdit,
    handleCreate: handleCreateNew,
    closeModal,
    handleSave,
    
    // Handlers del formulario
    onFormChange: handleFormChange,
    
    // Handlers originales (mantener para compatibilidad)
    openCreateModal,
    openViewModal,
    openEditModal,
    closeModals,
    handleFormChange,
    handleUpdate,
    handleDelete,
    
    // Utilidades
    refreshData,
    
    // Setters directos
    setPageNumber,
    setPageSize,
    setSearchValue
  };
};
